import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  ArrowBack as ArrowBackIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import {
  getAvailableHintVideos,
  getGroupHintVideo,
  getProblemGroupById,
  getSessionById,
  HintVideo,
  GroupHintVideo,
  ProblemGroup,
  Session
} from '../../services/sessionService';

// Single video view component
const VideoPlayer: React.FC<{ 
  videoUrl: string; 
  title: string; 
  groupId?: number; 
  sessionId?: number; 
  sessionTitle?: string;
  onBack?: () => void;
}> = ({ videoUrl, title, groupId, sessionId, sessionTitle, onBack }) => {
  // Transform YouTube URLs to embed format if needed
  const getEmbedUrl = (url: string): string => {
    // Handle both youtube.com/watch and youtu.be formats
    if (url.includes('youtube.com/watch')) {
      const videoId = new URL(url).searchParams.get('v');
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be')) {
      const videoId = url.split('/').pop();
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  return (
    <Box sx={{ mb: 4 }}>
      {onBack && (
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={onBack}
          sx={{ mb: 2 }}
        >
          Back to videos
        </Button>
      )}
      
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom>{title}</Typography>
        
        {sessionId && sessionTitle && (
          <Typography 
            variant="subtitle1" 
            color="text.secondary" 
            sx={{ mb: 2 }}
            component={RouterLink}
            to={`/sessions/${sessionId}`}
            style={{ textDecoration: 'none' }}
          >
            <SchoolIcon sx={{ fontSize: 'small', mr: 0.5, verticalAlign: 'middle' }} />
            {sessionTitle}
          </Typography>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
          <iframe
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0
            }}
            src={getEmbedUrl(videoUrl)}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </Box>
        
        {groupId && (
          <Box sx={{ mt: 3 }}>
            <Button 
              variant="contained" 
              color="primary"
              component={RouterLink}
              to={`/sessions/${sessionId}/problems`}
            >
              Practice Problems
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

// Video gallery component
const VideoGallery: React.FC<{ videos: HintVideo[]; onSelectVideo: (video: HintVideo) => void }> = ({ 
  videos, 
  onSelectVideo 
}) => {
  // Group videos by session
  const videosBySession: Record<number, { sessionTitle: string; videos: HintVideo[] }> = {};
  
  videos.forEach(video => {
    if (!videosBySession[video.sessionId]) {
      videosBySession[video.sessionId] = {
        sessionTitle: video.sessionTitle,
        videos: []
      };
    }
    videosBySession[video.sessionId].videos.push(video);
  });
  
  return (
    <Box sx={{ mb: 4 }}>
      {Object.keys(videosBySession).length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No hint videos are available yet. Check back later!
        </Alert>
      ) : (
        Object.entries(videosBySession).map(([sessionId, sessionData]) => (
          <Box key={sessionId} sx={{ mb: 4 }}>
            <Typography 
              variant="h6" 
              component={RouterLink}
              to={`/sessions/${sessionId}`}
              sx={{ 
                textDecoration: 'none', 
                display: 'flex', 
                alignItems: 'center',
                color: 'primary.main',
                mb: 2
              }}
            >
              <SchoolIcon sx={{ mr: 1 }} />
              {sessionData.sessionTitle}
            </Typography>
            
            <Grid container spacing={3}>
              {sessionData.videos.map(video => (
                <Grid item xs={12} sm={6} md={4} key={video.groupId}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardMedia
                      sx={{ 
                        height: 0, 
                        paddingTop: '56.25%', 
                        bgcolor: 'black',
                        position: 'relative'
                      }}
                      image={`https://img.youtube.com/vi/${getYouTubeVideoId(video.videoUrl)}/hqdefault.jpg`}
                      title={video.title}
                    >
                      <Box 
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.3)',
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.5)',
                            cursor: 'pointer'
                          }
                        }}
                        onClick={() => onSelectVideo(video)}
                      >
                        <PlayArrowIcon sx={{ fontSize: 60, color: 'white' }} />
                      </Box>
                    </CardMedia>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {video.title}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button 
                        size="small" 
                        startIcon={<PlayArrowIcon />}
                        onClick={() => onSelectVideo(video)}
                      >
                        Watch Video
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}
    </Box>
  );
};

// Helper function to extract YouTube video ID
function getYouTubeVideoId(url: string): string {
  try {
    if (url.includes('youtube.com/watch')) {
      return new URL(url).searchParams.get('v') || '';
    } else if (url.includes('youtu.be/')) {
      return url.split('youtu.be/')[1].split('?')[0];
    }
    return '';
  } catch (error) {
    console.error('Error parsing YouTube URL:', error);
    return '';
  }
}

// Main page component
interface HintVideosPageProps {
  groupId?: number;
}

const HintVideosPage: React.FC<HintVideosPageProps> = ({ groupId: propGroupId }) => {
  const { groupId: paramGroupId } = useParams<{ groupId?: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableVideos, setAvailableVideos] = useState<HintVideo[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<GroupHintVideo | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<ProblemGroup | null>(null);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  
  // Determine which group ID to use
  const groupIdToUse = propGroupId || (paramGroupId ? parseInt(paramGroupId, 10) : undefined);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (groupIdToUse) {
          // Fetch specific group's video
          const [video, group] = await Promise.all([
            getGroupHintVideo(groupIdToUse),
            getProblemGroupById(groupIdToUse)
          ]);
          
          if (!video) {
            setError('No hint video available for this problem group yet.');
          } else {
            setSelectedVideo(video);
            setSelectedGroup(group);
            
            // Also fetch the session details
            const session = await getSessionById(group.sessionId);
            setSelectedSession(session);
          }
        } else {
          // Fetch all available videos
          const videos = await getAvailableHintVideos();
          setAvailableVideos(videos);
        }
      } catch (err) {
        console.error('Error fetching hint videos:', err);
        setError('Failed to load hint videos. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [groupIdToUse]);
  
  const handleSelectVideo = async (video: HintVideo) => {
    try {
      setLoading(true);
      
      // Get full video and group details
      const [videoDetails, group, session] = await Promise.all([
        getGroupHintVideo(video.groupId),
        getProblemGroupById(video.groupId),
        getSessionById(video.sessionId)
      ]);
      
      if (videoDetails) {
        setSelectedVideo(videoDetails);
        setSelectedGroup(group);
        setSelectedSession(session);
      }
    } catch (err) {
      console.error('Error fetching video details:', err);
      setError('Failed to load video details. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBack = () => {
    setSelectedVideo(null);
    setSelectedGroup(null);
    setSelectedSession(null);
  };
  
  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ pt: 3, pb: 6 }}>
          {/* Only show page title when no video is selected or we're loading */}
          {(!selectedVideo || loading) && (
            <Typography variant="h4" component="h1" gutterBottom>
              {loading ? 'Loading...' : 'Hint Videos'}
            </Typography>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="info" sx={{ my: 2 }}>
              {error}
            </Alert>
          ) : selectedVideo ? (
            <VideoPlayer 
              videoUrl={selectedVideo.videoUrl} 
              title={selectedVideo.title}
              groupId={selectedGroup?.id}
              sessionId={selectedSession?.id}
              sessionTitle={selectedSession?.title}
              onBack={!groupIdToUse ? handleBack : undefined}
            />
          ) : (
            <VideoGallery 
              videos={availableVideos} 
              onSelectVideo={handleSelectVideo} 
            />
          )}
        </Box>
      </Container>
    </MainLayout>
  );
};

export default HintVideosPage;